import { QueryClientProvider } from "@tanstack/react-query"
import { uniqueId } from "lodash-es"
import {
  ComponentType,
  createContext,
  FC,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useRef,
} from "react"
import { createRoot, Root } from "react-dom/client"
import { ErrorBoundary, FallbackProps } from "react-error-boundary"
import { queryClient } from "../react-query"

const loaders = new Map<string, ComponentType>()
const IslandContext = createContext<string>("(no island)")

// Track islands by component name
const islandRegistry = new Map<string, Set<EvpReactIsland>>()

interface Island {
  name: string
  props: any
}
const Island: FC<Island> = ({ name, props }) => {
  const Component = loaders.get(name)
  if (!Component) {
    return `Did not find island file at "app/frontend/${name}.island.tsx"`
  }
  return (
    <IslandContext.Provider value={name}>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={null}>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Component {...props} />
          </ErrorBoundary>
        </Suspense>
      </QueryClientProvider>
    </IslandContext.Provider>
  )
}

const ErrorFallback: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const sent = useRef(false)
  const island = useContext(IslandContext)
  useEffect(() => {
    if (!sent.current) {
      sent.current = true
      const airbrake = window.airbrake
      if (airbrake) {
        airbrake.notify({
          error,
          context: { component: island },
        })
      }
    }
  }, [error, island])
  return (
    <div className="panel panel-danger" role="alert">
      <div className="panel-heading">
        <h4>Something went wrong</h4>
      </div>
      <div className="panel-body">
        <pre>{error.message}</pre>
        <button className="btn" onClick={resetErrorBoundary}>
          Try again
        </button>
      </div>
    </div>
  )
}

class EvpReactIsland extends HTMLElement {
  private _reactRoot?: Root
  static observedAttributes = ["props"]

  // Re-render all islands with a specific component name
  static rerenderIslandsByName(componentName: string) {
    const islands = islandRegistry.get(componentName)
    if (islands) {
      islands.forEach((island) => {
        island._renderReactRoot()
      })
    }
  }

  registerIsland() {
    const componentName = this.getAttribute("component")!
    if (!islandRegistry.has(componentName)) {
      islandRegistry.set(componentName, new Set())
    }
    islandRegistry.get(componentName)!.add(this)
  }

  unregisterIsland() {
    const componentName = this.getAttribute("component")!
    const islands = islandRegistry.get(componentName)
    if (islands) {
      islands.delete(this)
    }
  }

  connectedCallback() {
    const componentName = this.getAttribute("component")!
    this._reactRoot = createRoot(this, {
      // https://react.dev/reference/react/useId#specifying-a-shared-prefix-for-all-generated-ids
      identifierPrefix: uniqueId(`${componentName}【`) + "】",
    })
    this.registerIsland()
    this._renderReactRoot()
  }

  disconnectedCallback() {
    this.unregisterIsland()
    this._reactRoot?.unmount()
  }

  attributeChangedCallback(name: string, _oldValue: string, _newValue: string) {
    if (name === "props" && this.hasAttribute("rerender-on-props-change")) {
      this._renderReactRoot()
    }
  }

  private _renderReactRoot() {
    this._reactRoot?.render(
      <Island
        name={this.getAttribute("component")!}
        props={JSON.parse(this.getAttribute("props") || "{}")}
      />,
    )
  }
}

if (window.customElements) {
  window.customElements.define("evp-react-island", EvpReactIsland)

  // Add event listener for re-rendering islands by name
  document.addEventListener("rerenderReactIsland", ((
    event: CustomEvent<{ componentName: string }>,
  ) => {
    const { componentName } = event.detail || {}
    if (componentName) {
      EvpReactIsland.rerenderIslandsByName(componentName)
    }
  }) as EventListener)
}

export function registerIslandLoader(name: string, loader: () => Promise<any>) {
  loaders.set(name, lazy(loader))
}

// Export a helper function to trigger island re-rendering
export function rerenderIsland(componentName: string) {
  document.dispatchEvent(
    new CustomEvent("rerenderReactIsland", {
      detail: { componentName },
    }),
  )
}
